import { useState, useMemo } from "react";
import { FloorPlanZone } from "@/hooks/useFloorPlanZones";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GripVertical, Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface ZoneLayoutEditorProps {
  zones: FloorPlanZone[];
  onUpdateZonePosition: (zoneId: string, row: number, col: number) => void;
  onClose: () => void;
}

const GRID_SIZE = 3; // 3x3 grid for positioning

export const ZoneLayoutEditor = ({
  zones,
  onUpdateZonePosition,
  onClose,
}: ZoneLayoutEditorProps) => {
  const [draggedZone, setDraggedZone] = useState<FloorPlanZone | null>(null);
  const [gridRows, setGridRows] = useState(() => {
    const maxRow = Math.max(...zones.map(z => z.layout_row || 0), 1);
    return Math.max(maxRow + 1, 2);
  });
  const [gridCols, setGridCols] = useState(() => {
    const maxCol = Math.max(...zones.map(z => z.layout_col || 0), 1);
    return Math.max(maxCol + 1, 2);
  });

  // Create grid with zones placed
  const grid = useMemo(() => {
    const g: (FloorPlanZone | null)[][] = Array(gridRows)
      .fill(null)
      .map(() => Array(gridCols).fill(null));

    zones.forEach((zone) => {
      const row = zone.layout_row || 0;
      const col = zone.layout_col || 0;
      if (row < gridRows && col < gridCols) {
        g[row][col] = zone;
      }
    });

    return g;
  }, [zones, gridRows, gridCols]);

  // Zones not yet placed in the grid
  const unplacedZones = useMemo(() => {
    return zones.filter((zone) => {
      const row = zone.layout_row || 0;
      const col = zone.layout_col || 0;
      return row >= gridRows || col >= gridCols || grid[row][col]?.id !== zone.id;
    });
  }, [zones, grid, gridRows, gridCols]);

  const handleDragStart = (zone: FloorPlanZone) => {
    setDraggedZone(zone);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (row: number, col: number) => {
    if (draggedZone) {
      onUpdateZonePosition(draggedZone.id, row, col);
      setDraggedZone(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedZone(null);
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        Glissez les zones pour définir leur position dans la vue d'ensemble.
      </div>

      {/* Grid size controls */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span>Colonnes:</span>
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={() => setGridCols(Math.max(1, gridCols - 1))}
            disabled={gridCols <= 1}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-4 text-center">{gridCols}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={() => setGridCols(Math.min(5, gridCols + 1))}
            disabled={gridCols >= 5}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span>Lignes:</span>
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={() => setGridRows(Math.max(1, gridRows - 1))}
            disabled={gridRows <= 1}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-4 text-center">{gridRows}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={() => setGridRows(Math.min(5, gridRows + 1))}
            disabled={gridRows >= 5}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Layout grid */}
      <Card className="p-4">
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gridTemplateRows: `repeat(${gridRows}, 80px)`,
          }}
        >
          {grid.map((row, rowIndex) =>
            row.map((zone, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={cn(
                  "border-2 border-dashed rounded-lg flex items-center justify-center transition-all",
                  zone
                    ? "border-solid bg-muted/50"
                    : "border-muted-foreground/30 hover:border-primary/50 hover:bg-primary/5",
                  draggedZone && !zone && "border-primary/50 bg-primary/10"
                )}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(rowIndex, colIndex)}
              >
                {zone ? (
                  <div
                    draggable
                    onDragStart={() => handleDragStart(zone)}
                    onDragEnd={handleDragEnd}
                    className={cn(
                      "w-full h-full p-2 flex flex-col items-center justify-center cursor-grab active:cursor-grabbing rounded-md transition-opacity",
                      draggedZone?.id === zone.id && "opacity-50"
                    )}
                    style={{ backgroundColor: `${zone.color}20` }}
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground mb-1" />
                    <div
                      className="w-3 h-3 rounded-full mb-1"
                      style={{ backgroundColor: zone.color }}
                    />
                    <span className="text-xs font-medium text-center line-clamp-2">
                      {zone.name}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {rowIndex},{colIndex}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Unplaced zones */}
      {unplacedZones.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">Zones non placées:</div>
          <div className="flex flex-wrap gap-2">
            {unplacedZones.map((zone) => (
              <div
                key={zone.id}
                draggable
                onDragStart={() => handleDragStart(zone)}
                onDragEnd={handleDragEnd}
                className={cn(
                  "px-3 py-2 rounded-lg border cursor-grab active:cursor-grabbing flex items-center gap-2 transition-opacity",
                  draggedZone?.id === zone.id && "opacity-50"
                )}
                style={{ borderColor: zone.color, backgroundColor: `${zone.color}10` }}
              >
                <GripVertical className="h-3 w-3 text-muted-foreground" />
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: zone.color }}
                />
                <span className="text-sm">{zone.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={onClose}>Terminé</Button>
      </div>
    </div>
  );
};
